/**
 * useAgreements
 *
 * The commitments this tenant still owes an acceptance for. The backend refuses machine
 * creation while one is outstanding, so this is what gives the customer a way to clear it.
 *
 * Each commitment carries its own feature flag, and the outstanding list already respects
 * them server-side — a commitment that is switched off is simply absent. The flags are read
 * here only to avoid asking the API at all when neither is on.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  agreementService,
  type AgreementDto,
  type AgreementType,
} from "@/services/agreement.service";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { CLIENT_FEATURE_FLAG_KEYS } from "@/services/config.service";
import { useAuthStore } from "@/stores/authStore";

export const AGREEMENTS_QUERY_KEY = ["agreements", "outstanding"] as const;

function useAnyAgreementEnabled(): boolean {
  const { data } = useFeatureFlags();
  const flags = data?.flags;
  return (
    (flags?.[CLIENT_FEATURE_FLAG_KEYS.agreementSignup]?.enabled ?? false) ||
    (flags?.[CLIENT_FEATURE_FLAG_KEYS.agreementVmCreation]?.enabled ?? false)
  );
}

export function useOutstandingAgreements() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const anyEnabled = useAnyAgreementEnabled();

  return useQuery({
    queryKey: AGREEMENTS_QUERY_KEY,
    queryFn: agreementService.getOutstanding,
    enabled: isAuthenticated && anyEnabled,
    // Short-lived on purpose: an operator publishing a new revision should reach open sessions
    // on their next navigation rather than only after a reload.
    staleTime: 60 * 1000,
  });
}

/** The outstanding entry for one commitment, or undefined when nothing is owed for it. */
export function useOutstandingAgreement(
  type: AgreementType,
): AgreementDto | undefined {
  const { data } = useOutstandingAgreements();
  return data?.find((agreement) => agreement.type === type);
}

/**
 * The first commitment blocking creation, whichever it is.
 *
 * Mirrors the backend's single creation gate: every switched-on commitment has to be signed
 * before a machine is created, not only the one named after machine creation. Asking for a
 * specific type here instead would let the customer through a dialog and straight into an
 * AGREEMENT_REQUIRED they have no way to clear.
 *
 * The list arrives in declaration order, so the software-usage commitment comes first — the same
 * order the console asks for them in.
 */
export function useCreationBlockingAgreement(): AgreementDto | undefined {
  const { data } = useOutstandingAgreements();
  return data?.[0];
}

export function useAcceptAgreement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agreement,
      culture,
    }: {
      agreement: AgreementDto;
      culture: string;
    }) => agreementService.accept(agreement, culture),
    onSuccess: () => {
      // Refetch rather than patch the cache: the backend is the authority on what is still
      // outstanding, and a second commitment may become due in the same breath.
      void queryClient.invalidateQueries({ queryKey: AGREEMENTS_QUERY_KEY });
    },
  });
}
